interface Props { cs: boolean }

interface Row {
  symptom: string
  cause: string
  fix: string
}

const CS_ROWS: Row[] = [
  {
    symptom: 'Stanice nevidí host — "showx.local:5300 nedosažitelný"',
    cause: 'mDNS blokované nebo jiný LAN segment',
    fix: 'Zadej manuální IP: v shellu, záložka Show → STATIONS panel. Na iPadu otevři http://<ip>:5300. Ověř, že všechna zařízení jsou na stejné Wi-Fi / VLAN.',
  },
  {
    symptom: 'Pairing selže — PIN odmítnutý nebo vyprší',
    cause: 'Špatný PIN, vypršení 10minutového časového okna, nebo testovací mód není aktivní',
    fix: 'Zobraz nový PIN v shellu (Show → STATIONS). V testovacím módu (SHOWX_PAIRING_TEST_PIN=123456) PIN nikdy nevyprší. Zkontroluj přesný opis 6 číslic.',
  },
  {
    symptom: 'Cue nevystřelí — Dispatch Log prázdný nebo žádná odpověď',
    cause: 'Špatná GO autorita, cue nemá payload, nebo routing chybí',
    fix: 'Zkontroluj go_authority cuelistu (SHOW mód + sm_called = pouze SM stanice může GO). Ověř, že cue má alespoň jeden payload. Zkontroluj routing table v shellu — device host/port musí být správné.',
  },
  {
    symptom: 'OSC device neodpovídá — Dispatch Log ukazuje error',
    cause: 'Špatná IP/port v routing table, device offline, firewall na target Macu',
    fix: 'V shellu → Devices, otevři detail zařízení, ověř host a port. Pinguj target IP. Restartuj target device. Zkontroluj macOS Firewall na target Macu (QLab, Eos apod.).',
  },
  {
    symptom: 'LTC lock nechytá — indikátor červený nebo žlutý',
    cause: 'Nízká úroveň audio signálu, špatný audio vstup, nebo LTC frame rate neshoduje',
    fix: 'V shellu → Devices → Clock, ověř vybraný audio vstup (LTC kabel musí jít do správného kanálu). Zvyš úroveň LTC signálu na zdroji (ideálně -12 dBFS). Zkontroluj frame rate: ShowX auto-detekuje 25/30/29.97 DF. Čekej 2 konsekutivní validní framy pro lock.',
  },
  {
    symptom: 'SHOW mód — editace zamčená, nelze změnit payload',
    cause: 'Show je v SHOW módu; payloady jsou zmrazeny',
    fix: 'Přepni do REHEARSAL (SM klikne na mode badge → potvrdit). Po přepnutí lze payloady editovat normálně. Alternativně: vytvoř Proposal (v SHOW módu) — SM může schválit změnu bez opuštění SHOW módu.',
  },
  {
    symptom: 'Agent hub nespojený — indikátor "agent" v patičce nesvítí',
    cause: 'MCP server není spuštěný, špatný port, nebo ShowX nelistenuje na 9441',
    fix: 'Ověř, že MCP balík je buildnutý (pnpm --filter @showx/mcp build). Zkontroluj, že ShowX běží a port 9441 je volný (lsof -i :9441). Restartuj Claude Code nebo Claude Desktop pro znovu-připojení. Proměnná SHOWX_CONTROL_HUB_PORT musí být stejná na obou stranách (default 9441).',
  },
]

const EN_ROWS: Row[] = [
  {
    symptom: 'Station cannot find host — "showx.local:5300 unreachable"',
    cause: 'mDNS blocked or different LAN segment',
    fix: 'Enter manual IP: in the shell, Show → STATIONS panel. On iPad open http://<ip>:5300. Verify all devices are on the same Wi-Fi / VLAN.',
  },
  {
    symptom: 'Pairing fails — PIN rejected or expires',
    cause: 'Wrong PIN, 10-minute expiry window passed, or test mode not active',
    fix: 'Show a new PIN in the shell (Show → STATIONS). In test mode (SHOWX_PAIRING_TEST_PIN=123456) the PIN never expires. Check the exact 6 digits.',
  },
  {
    symptom: 'Cue does not fire — Dispatch Log empty or no response',
    cause: 'Wrong GO authority, cue has no payload, or routing is missing',
    fix: 'Check cuelist go_authority (SHOW mode + sm_called = only SM station can GO). Verify the cue has at least one payload. Check the routing table in the shell — device host/port must be correct.',
  },
  {
    symptom: 'OSC device not responding — Dispatch Log shows error',
    cause: 'Wrong IP/port in routing table, device offline, firewall on target Mac',
    fix: 'In shell → Devices, open device detail, verify host and port. Ping the target IP. Restart target device. Check macOS Firewall on the target Mac (QLab, Eos, etc.).',
  },
  {
    symptom: 'LTC lock not catching — indicator stays red or yellow',
    cause: 'Low audio signal level, wrong audio input, or frame rate mismatch',
    fix: 'In shell → Devices → Clock, verify the selected audio input (LTC cable must go to the correct channel). Increase LTC signal level at source (ideally -12 dBFS). Check frame rate: ShowX auto-detects 25/30/29.97 DF. Wait for 2 consecutive valid frames for lock.',
  },
  {
    symptom: 'SHOW mode — editing locked, cannot change payload',
    cause: 'Show is in SHOW mode; payloads are frozen',
    fix: 'Switch to REHEARSAL (SM clicks mode badge → confirm). Payloads can be edited normally after the switch. Alternatively: create a Proposal (in SHOW mode) — SM can approve the change without leaving SHOW mode.',
  },
  {
    symptom: 'Agent hub not connected — "agent" indicator in footer is off',
    cause: 'MCP server not running, wrong port, or ShowX not listening on 9441',
    fix: 'Verify the MCP package is built (pnpm --filter @showx/mcp build). Check that ShowX is running and port 9441 is free (lsof -i :9441). Restart Claude Code or Claude Desktop to reconnect. SHOWX_CONTROL_HUB_PORT must match on both sides (default 9441).',
  },
]

function TroubleTable({ rows, cs }: { rows: Row[]; cs: boolean }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="border-b border-rule">
            <th className="text-left py-3 pr-4 font-medium text-muted w-1/4">
              {cs ? 'Příznak' : 'Symptom'}
            </th>
            <th className="text-left py-3 pr-4 font-medium text-muted w-1/4">
              {cs ? 'Příčina' : 'Cause'}
            </th>
            <th className="text-left py-3 font-medium text-muted">
              {cs ? 'Řešení' : 'Fix'}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-rule/50 align-top">
              <td className="py-4 pr-4 text-ink">{r.symptom}</td>
              <td className="py-4 pr-4 text-muted">{r.cause}</td>
              <td className="py-4 text-ink">{r.fix}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export default function Chapter10({ cs }: Props) {
  return (
    <article id="kap-10" className="scroll-mt-24">
      <h2 className="display-serif text-display-2 leading-tight mb-8">
        {cs ? '10. Troubleshooting' : '10. Troubleshooting'}
      </h2>
      <div className="copy prose-guide max-w-none">
        <p>
          {cs
            ? 'Přehled nejčastějších problémů, jejich příčin a postupu řešení. Situace jsou odvozeny z reálných spec a testovacích poznatků.'
            : 'Overview of the most common problems, their causes, and how to fix them. Situations are derived from real spec and test findings.'}
        </p>
        <div className="mt-8">
          <TroubleTable rows={cs ? CS_ROWS : EN_ROWS} cs={cs} />
        </div>
      </div>
    </article>
  )
}
